import { useQuery } from '@tanstack/react-query';
import { BillingClient } from 'network/httpClients/BillingClient/BillingClient';
import { QUERY_KEYS } from 'network/queries/consts';
import { getDeploymentConfig, isBillingAvailable } from 'services/config';
import { ACCOUNT_INFO_FIXTURES } from 'test/fixtures/billing/accountInfoFixtures';
import { getQaBillingState } from 'test/fixtures/billing/quotaFixtures';
import { AccountInfoResponse } from 'types/billing';

// Query-key discriminators: keep the fixture-driven QA cache entry separate
// from the real endpoint so switching states never serves a stale cache
// (mirrors useQuota).
const ACCOUNT_INFO_SCOPE = 'account-info';
const REAL_SOURCE_KEY = 'real';

/**
 * Returns GET /billing/v1/account-info as an AccountInfoResponse: trial +
 * account-level data (selfServe, tier, stripeCustomerId), sourced from the
 * Firestore account/workspace docs — a different data domain than /quota
 * (standup D22, PLT-4042).
 *
 * Mock mode mirrors useQuota: when the URL carries ?qa_billing_state=<state>,
 * the matching fixture is returned synchronously. The SAME param drives both
 * hooks so /quota and /account-info stay in lockstep when QA-ing a state.
 *
 * Freshness is coupled to useQuota (D26): same staleTime + refetchOnMount +
 * refetchOnWindowFocus so a single trigger (billing entry / focus / post-action
 * / post-checkout poll) refreshes both.
 */
export const useAccountInfo = (options?: { enabled?: boolean }) => {
  const qaState = getQaBillingState();
  const billingAvailable = isBillingAvailable(getDeploymentConfig());

  return useQuery<AccountInfoResponse>({
    queryKey: [
      QUERY_KEYS.BILLING_ACCOUNT_INFO,
      ACCOUNT_INFO_SCOPE,
      qaState ?? REAL_SOURCE_KEY,
    ],
    queryFn: async () => {
      if (qaState !== undefined) {
        return ACCOUNT_INFO_FIXTURES[qaState];
      }

      return await BillingClient.getAccountInfo();
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    enabled: billingAvailable && (options?.enabled ?? true),
  });
};
